import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ClientData } from '../types';
import { getPrefilledClientData, profileSettingsItems, profileSettingsOptions } from '../utils';
import { useSources } from '../context/SourcesContext';
import { useToast } from '../context/ToastContext';
import {
  combineClientData,
  EMPTY_FIELD_ERROR_MESSAGE,
  findEmptyVisibleFields,
  VISIBLE_PROJECT_SETTINGS_FIELDS,
} from '../components/settings/clientDataForm';
import ProjectSettingsFields from '../components/settings/ProjectSettingsFields';
import { Button } from '../uikit';

// The COM-638 settings page: all field blocks rendered at once (not the
// dialog's stepper), one Save button. Reuses the exact field metadata and
// presentational block (`ProjectSettingsFields`) the onboarding dialog uses,
// so both surfaces stay visually and behaviourally in sync. Saves via
// `updateProjectData` (no auto-discovery) — never `setProjectData`, which is
// the onboarding-only path that also kicks off source auto-discovery.
// COM-647 Task 3: this page's toast auto-hides after 3s - moved onto the
// shared `ToastContext` host (see that file's own doc comment on why
// `autoHideMs` is opt-in per caller rather than a host-wide policy).
const SETTINGS_TOAST_AUTO_HIDE_MS = 3000;

const SettingsPage: React.FC = () => {
  const { selectedProject, canManageProject, updateProjectData } = useSources();
  const { toast, showToast, hideToast } = useToast();

  const [clientData, setClientData] = useState<ClientData>(getPrefilledClientData([]));
  const [customOptions, setCustomOptions] = useState<ClientData>(getPrefilledClientData([]));
  const [selectedOptionsInputs, setSelectedOptionsInputs] = useState<{ [key: string]: string }>(
    getPrefilledClientData('')
  );

  // Same derivation as ClientDataDialog's first useEffect: pull the
  // profile-settings fields off the selected project.
  useEffect(() => {
    const savedClientData = selectedProject
      ? Object.entries(selectedProject).reduce((res, [key, value]) => {
        if (profileSettingsItems.includes(key as keyof ClientData)) {
          res[key as keyof ClientData] = value;
        }
        return res;
      }, {} as ClientData)
      : getPrefilledClientData([]);
    setClientData(savedClientData);
  }, [selectedProject]);

  // Same derivation as ClientDataDialog's second useEffect: whatever isn't a
  // known predefined option is treated as a custom addition.
  useEffect(() => {
    if (clientData) {
      const selectedCustomOptions = Object.entries(clientData).reduce(
        (res, [type, options]) => {
          const optionList = options.filter(
            (option: string) => !profileSettingsOptions[type as keyof ClientData].includes(option)
          );
          res[type as keyof ClientData] = Array.from(new Set(optionList));
          return res;
        },
        {} as Record<keyof ClientData, string[]>
      );

      setCustomOptions(selectedCustomOptions);
    }
  }, [clientData]);

  // Clears THIS page's own validation-error toast as soon as every visible
  // field has either a predefined selection or a custom option - checked
  // against the exact message (not just `severity === 'error'`) since `toast`
  // is now shared, app-wide state (ToastContext): some OTHER surface's error
  // toast (e.g. a socket failure) could be showing while the user is on this
  // page, and this effect must not clear THAT one just because this page's
  // own fields happen to already be complete.
  useEffect(() => {
    if (toast?.severity !== 'error' || toast.message !== EMPTY_FIELD_ERROR_MESSAGE) return;
    if (findEmptyVisibleFields(clientData, customOptions).length === 0) {
      hideToast();
    }
  }, [clientData, customOptions, toast, hideToast]);

  const canManage = canManageProject();

  const handleCheckboxChange =
    (field: keyof ClientData, value: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      const combinedData = combineClientData(clientData, customOptions);
      setClientData(() => ({
        ...combinedData,
        [field]: checked
          ? [...combinedData[field], value]
          : combinedData[field].filter((item) => item !== value),
      }));
    };

  const handleRemoveCustomItem = (field: keyof ClientData, item: string) => {
    setCustomOptions((prev) => ({
      ...prev,
      [field]: prev[field].filter((i) => i !== item),
    }));
  };

  const handleAddSelectedOption = (field: keyof ClientData) => {
    const customValue = selectedOptionsInputs[field]?.trim();
    if (customValue && !customOptions[field].includes(customValue) && !clientData[field].includes(customValue)) {
      setCustomOptions((prev) => ({
        ...prev,
        [field]: [...prev[field], customValue],
      }));
      setSelectedOptionsInputs((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSave = () => {
    if (findEmptyVisibleFields(clientData, customOptions).length > 0) {
      showToast(EMPTY_FIELD_ERROR_MESSAGE, 'error', { autoHideMs: SETTINGS_TOAST_AUTO_HIDE_MS });
      return;
    }
    const combinedData = combineClientData(clientData, customOptions);
    updateProjectData(combinedData);
    showToast('Settings saved.', 'success', { autoHideMs: SETTINGS_TOAST_AUTO_HIDE_MS });
  };

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          You do not have permission to change these settings.
        </Typography>
      </Box>
    );
  }

  return (
    <Box data-testid="settings-page" sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
      <Typography variant="h5" gutterBottom>
        Project Settings
      </Typography>

      {VISIBLE_PROJECT_SETTINGS_FIELDS.map(({ field, title, description, options, disabled }) => (
        <ProjectSettingsFields
          key={field}
          field={field}
          title={title}
          description={description}
          options={options}
          clientData={clientData}
          customOptions={customOptions}
          selectedOptionsInputs={selectedOptionsInputs}
          onCheckboxChange={handleCheckboxChange}
          onCustomOptionAdd={handleAddSelectedOption}
          onCustomOptionRemove={handleRemoveCustomItem}
          onOptionInputChange={(changedField, value) =>
            setSelectedOptionsInputs((prev) => ({
              ...prev,
              [changedField]: value,
            }))
          }
          disabled={disabled}
          canManage={canManage}
        />
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
        <Button onClick={handleSave}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default SettingsPage;
